/**
 * Preferencias de quem usa — guardadas no proprio navegador.
 *
 * A POSICAO DA BARRA E ESCOLHA DO USUARIO, e isso e padrao do sistema, nao
 * enfeite. Fiscal que passa o dia na tela tem preferencia formada, e um
 * sistema que impoe o proprio arranjo transforma preferencia em queixa.
 *
 * `localStorage` e nao `sessionStorage`, ao contrario da credencial: a
 * credencial deve morrer ao fechar a aba (computador compartilhado de
 * secretaria); a preferencia de layout deve sobreviver, porque e da PESSOA e
 * nao da sessao. Ela nao e dado sensivel — e a posicao de um menu.
 *
 * TODA LEITURA E TODA ESCRITA VAO EM `try`: navegador com armazenamento
 * bloqueado (janela anonima, politica de grupo) faz o acessor ESTOURAR, e nao
 * apenas devolver vazio. Sem o `try`, a tela inteira morreria por causa da
 * posicao de um menu.
 */

export const POSICOES = [
  "topo",
  "esquerda",
  "direita",
  "topo-esquerda",
  "topo-direita",
] as const

export type PosicaoDaBarra = (typeof POSICOES)[number]

export const NOME_DA_POSICAO: Record<PosicaoDaBarra, string> = {
  topo: "Barra no topo",
  esquerda: "Trilho à esquerda",
  direita: "Trilho à direita",
  "topo-esquerda": "Topo + trilho à esquerda",
  "topo-direita": "Topo + trilho à direita",
}

const CHAVE = "contratos.posicao-da-barra"

/** O padrao. Escolhido por DEV em 01/09/2026 para a apresentação. */
export const POSICAO_PADRAO: PosicaoDaBarra = "topo"

function ehPosicao(valor: unknown): valor is PosicaoDaBarra {
  return typeof valor === "string" && (POSICOES as readonly string[]).includes(valor)
}

export function posicaoGuardada(): PosicaoDaBarra {
  try {
    const bruto = localStorage.getItem(CHAVE)
    return ehPosicao(bruto) ? bruto : POSICAO_PADRAO
  } catch {
    return POSICAO_PADRAO
  }
}

export function guardarPosicao(posicao: PosicaoDaBarra): void {
  try {
    localStorage.setItem(CHAVE, posicao)
  } catch {
    /* sem armazenamento, a escolha vale so enquanto a pagina estiver aberta */
  }
}

// --- APARENCIA: MODO, COR, TONALIDADE, LETRA E CONTRASTE (spec 0123) --------
//
// A escolha mora no navegador, como sempre morou o tema: e preferencia de
// operacao, nao dado de negocio. Tudo vira atributo no <html>, e o CSS
// (estilo/temas.css e estilo/aparencia.css) reage a eles. O modo continua no
// `data-tema` de antes, so presente no escuro, para nao mexer em nenhum
// seletor `[data-tema="escuro"]` que ja existe.
//
// O script do <head> do index.html repete esta leitura (e a validacao) para o
// tema valer antes da primeira pintura; o teste `sem-piscar` garante que os
// dois concordam.

export const CORES = ["azul", "verde", "vinho", "roxo", "grafite", "ambar"] as const
export const TONS = ["suave", "media", "profunda"] as const

export type Tema = "claro" | "escuro"
export type Cor = (typeof CORES)[number]
export type Tom = (typeof TONS)[number]
export type Fonte = "normal" | "grande"
export type Contraste = "normal" | "alto"
export type Familia = "inter" | "plex"

export type Aparencia = {
  modo: Tema
  cor: Cor
  tom: Tom
  fonte: Fonte
  contraste: Contraste
  familia: Familia
}

export const APARENCIA_PADRAO: Aparencia = {
  modo: "claro",
  cor: "azul",
  tom: "media",
  fonte: "normal",
  contraste: "normal",
  familia: "inter",
}

export const NOME_DA_COR: Record<Cor, string> = {
  azul: "Azul",
  verde: "Verde",
  vinho: "Vinho",
  roxo: "Roxo",
  grafite: "Grafite",
  ambar: "Âmbar",
}

export const NOME_DA_FAMILIA: Record<Familia, string> = {
  inter: "Inter",
  plex: "IBM Plex Sans",
}

export const NOME_DO_TOM: Record<Tom, string> = {
  suave: "Suave",
  media: "Média",
  profunda: "Profunda",
}

const CHAVE_DA_APARENCIA = "contratos.aparencia"
/** A chave de antes da spec 0123: so lida, para quem ja tinha o escuro. */
const CHAVE_DO_TEMA = "contratos.tema"

export const APARENCIA_TROCADA = "aparencia-trocada"
export const TEMA_TROCADO = "tema-trocado"

function validar(bruto: unknown, modoAntigo: Tema): Aparencia {
  const p = (typeof bruto === "object" && bruto !== null ? bruto : {}) as Record<string, unknown>
  return {
    modo: p.modo === "escuro" || p.modo === "claro" ? p.modo : modoAntigo,
    cor: (CORES as readonly unknown[]).includes(p.cor) ? (p.cor as Cor) : APARENCIA_PADRAO.cor,
    tom: (TONS as readonly unknown[]).includes(p.tom) ? (p.tom as Tom) : APARENCIA_PADRAO.tom,
    fonte: p.fonte === "grande" ? "grande" : "normal",
    contraste: p.contraste === "alto" ? "alto" : "normal",
    familia: p.familia === "plex" ? "plex" : "inter",
  }
}

export function aparenciaGuardada(): Aparencia {
  try {
    const modoAntigo: Tema = localStorage.getItem(CHAVE_DO_TEMA) === "escuro" ? "escuro" : "claro"
    const bruta = localStorage.getItem(CHAVE_DA_APARENCIA)
    if (bruta === null) return { ...APARENCIA_PADRAO, modo: modoAntigo }
    try {
      return validar(JSON.parse(bruta), APARENCIA_PADRAO.modo)
    } catch {
      return { ...APARENCIA_PADRAO }
    }
  } catch {
    return { ...APARENCIA_PADRAO }
  }
}

function atributo(raiz: HTMLElement, nome: string, valor: string | null) {
  if (valor === null) raiz.removeAttribute(nome)
  else raiz.setAttribute(nome, valor)
}

/** Aplica no <html>, guarda e avisa. Devolve `false` quando o navegador nao
 *  deixou guardar: a escolha vale assim mesmo, ate a pagina fechar. */
export function aplicarAparencia(aparencia: Aparencia): boolean {
  const raiz = document.documentElement
  atributo(raiz, "data-tema", aparencia.modo === "escuro" ? "escuro" : null)
  atributo(raiz, "data-cor", aparencia.cor)
  atributo(raiz, "data-tom", aparencia.tom)
  atributo(raiz, "data-fonte", aparencia.fonte === "grande" ? "grande" : null)
  atributo(raiz, "data-contraste", aparencia.contraste === "alto" ? "alto" : null)
  atributo(raiz, "data-familia", aparencia.familia === "plex" ? "plex" : null)
  let guardou = true
  try {
    localStorage.setItem(CHAVE_DA_APARENCIA, JSON.stringify(aparencia))
  } catch {
    guardou = false
  }
  window.dispatchEvent(new CustomEvent(APARENCIA_TROCADA, { detail: aparencia }))
  window.dispatchEvent(new CustomEvent(TEMA_TROCADO, { detail: aparencia.modo }))
  return guardou
}

export function mudarAparencia(parcial: Partial<Aparencia>): boolean {
  return aplicarAparencia({ ...aparenciaGuardada(), ...parcial })
}

// --- TEMA CLARO OU ESCURO ---------------------------------------------------
//
// O tema de antes virou o MODO da aparencia. As duas funcoes ficam porque a
// Casca (o botao da barra do topo) e outras telas as usam: elas leem e trocam
// so o modo, e a cor, a tonalidade, a letra e o contraste ficam como estao.

export function temaGuardado(): Tema {
  return aparenciaGuardada().modo
}

export function aplicarTema(tema: Tema): void {
  mudarAparencia({ modo: tema })
}
